import type { SiteEdition } from '~/types/site'
import type { IRoleRights } from '~/services/BasicServer/types'
import type { proConfigItem } from '~/services/ProServer/types'

export type EnterprisePricingMode = 'local' | 'global'
export type EnterpriseCellValue = string | boolean

export interface EnterpriseFeature {
  key: 'document' | 'image' | 'editing' | 'history' | 'glossary'
  label: string
  title: string
  description: string
  bullets: string[]
  action: string
}

export interface EnterprisePlan {
  name: string
  quota: string
  price: string
  period: string
  features: string[]
  action: string
  customized?: boolean
  choiceId?: string
}

export type EnterpriseRightsKey = 'base' | 'middle' | 'high' | 'global_base' | 'global_middle' | 'global_high'

export interface EnterpriseCatalogData {
  currency?: string
  localPrices?: proConfigItem[]
  globalPrices?: proConfigItem[]
  rights?: Partial<Record<EnterpriseRightsKey, IRoleRights>> | null
}

export interface EnterpriseComparisonSection {
  title: string
  rows: Array<{
    label: string
    values: EnterpriseCellValue[]
  }>
}

export interface EnterpriseFaq {
  question: string
  answer: string
}

export type EnterpriseTranslate = (key: string, named?: Record<string, string | number>) => string

const featureKeys: EnterpriseFeature['key'][] = ['document', 'image', 'editing', 'history', 'glossary']
const baseFaqKeys = ['difference', 'glossary', 'characters', 'storage']
const trailingFaqKeys = ['seats', 'quota', 'formats', 'pricing']

function getFaq(t: EnterpriseTranslate, key: string): EnterpriseFaq {
  return {
    question: t(`enterprise_home_faq_${key}_question`),
    answer: t(`enterprise_home_faq_${key}_answer`),
  }
}

export function getEnterpriseTestimonials(t: EnterpriseTranslate) {
  return [
    {
      organization: t('enterprise_home_testimonial_harvard_organization'),
      role: t('enterprise_home_testimonial_harvard_role'),
      quote: t('enterprise_home_testimonial_harvard'),
    },
    {
      organization: t('enterprise_home_testimonial_mayo_organization'),
      role: t('enterprise_home_testimonial_mayo_role'),
      quote: t('enterprise_home_testimonial_mayo'),
    },
    {
      organization: t('enterprise_home_testimonial_novartis_organization'),
      role: t('enterprise_home_testimonial_novartis_role'),
      quote: t('enterprise_home_testimonial_novartis'),
    },
  ]
}

export function getEnterpriseFeatures(t: EnterpriseTranslate): EnterpriseFeature[] {
  return featureKeys.map(key => ({
    key,
    label: t(`enterprise_home_feature_${key}_label`),
    title: t(`enterprise_home_feature_${key}_title`),
    description: t(`enterprise_home_feature_${key}_description`),
    bullets: [1, 2, 3].map(index => t(`enterprise_home_feature_${key}_bullet_${index}`)),
    action: t(`enterprise_home_feature_${key}_action`),
  }))
}

const localPlanProfiles = [
  { nameKey: 'enterprise_home_plan_base', priceLevel: 'base', rightsKey: 'base' as const },
  { nameKey: 'enterprise_home_plan_professional', priceLevel: 'mid', rightsKey: 'middle' as const },
  { nameKey: 'enterprise_home_plan_advanced', priceLevel: 'high', rightsKey: 'high' as const },
]

const globalPlanProfiles = [
  { nameKey: 'enterprise_home_plan_base', priceLevel: 'base', rightsKey: 'global_base' as const },
  { nameKey: 'enterprise_home_plan_professional', priceLevel: 'professional', rightsKey: 'global_middle' as const },
  { nameKey: 'enterprise_home_plan_advanced', priceLevel: 'advanced', rightsKey: 'global_high' as const },
]

const priceSymbols: Record<string, string> = {
  cny: '¥',
  usd: '$',
  eur: '€',
  gbp: '£',
  jpy: '¥',
  krw: '₩',
  hkd: 'HK$',
  twd: 'NT$',
}

export function getEnterpriseCurrencySymbol(currency: string) {
  return priceSymbols[currency] || currency.toUpperCase()
}

function formatNumber(value: number, locale = 'zh-CN', maximumFractionDigits = 2) {
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value)
}

function formatCharacters(value: number, locale = 'zh-CN') {
  if (value >= 10_000) {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 }).format(value)
  }
  return formatNumber(value, locale)
}

function formatCharactersFromRights(value: number | undefined, locale = 'zh-CN') {
  return typeof value === 'number' && Number.isFinite(value) ? formatCharacters(value, locale) : '--'
}

function formatRightsNumber(value: number | null | undefined, locale = 'zh-CN') {
  return typeof value === 'number' && Number.isFinite(value) ? formatNumber(value, locale) : '--'
}

function formatPrice(value: number, currency: string, locale = 'zh-CN') {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return ''
  return `${getEnterpriseCurrencySymbol(currency)}${formatNumber(amount, locale)}`
}

function findPrice(prices: proConfigItem[] | undefined, level: string, currency: string) {
  return prices?.find(item => item.level === level && item.currency === currency && item.interval === '1year')
}

function getRightsProfiles(mode: EnterprisePricingMode, catalog?: EnterpriseCatalogData) {
  const keys: EnterpriseRightsKey[] = mode === 'global'
    ? ['global_base', 'global_middle', 'global_high']
    : ['base', 'middle', 'high']
  return keys.map(key => catalog?.rights?.[key])
}

export function getEnterprisePlans(
  edition: SiteEdition,
  mode: EnterprisePricingMode,
  catalog: EnterpriseCatalogData | undefined,
  t: EnterpriseTranslate,
  locale = 'zh-CN',
): EnterprisePlan[] {
  const isVip = edition === 'vip'
  const currency = catalog?.currency || (isVip ? 'cny' : 'usd')
  const getDisplayPrice = (backendPrice: proConfigItem | undefined) => backendPrice
    ? formatPrice(backendPrice.realPrice, currency, locale)
    : '--'
  const crossRegion = isVip
    ? t('enterprise_home_plan_cross_region_ai')
    : t('enterprise_home_plan_cross_region_vip')

  if (mode === 'global') {
    const plans = globalPlanProfiles.map((profile) => {
      const rights = catalog?.rights?.[profile.rightsKey]
      const backendPrice = findPrice(catalog?.globalPrices, profile.priceLevel, currency)
      return {
        name: t(profile.nameKey),
        quota: t('enterprise_home_plan_quota_global', { count: formatCharactersFromRights(rights?.charsMaxLimit, locale) }),
        price: getDisplayPrice(backendPrice),
        period: t('enterprise_home_plan_period'),
        features: [
          t('enterprise_home_plan_seats_global', { count: formatRightsNumber(rights?.memberCountLimit, locale) }),
          crossRegion,
          t('enterprise_home_plan_batch', { count: formatRightsNumber(rights?.batchTransMaxLimit, locale) }),
        ],
        action: t('enterprise_home_plan_action_open'),
        choiceId: backendPrice?.choiceId,
      }
    })

    return plans.concat({
      name: t('enterprise_home_plan_custom'),
      quota: t('enterprise_home_plan_custom_quota'),
      price: t('enterprise_home_plan_custom_price'),
      period: t('enterprise_home_plan_custom_period'),
      features: [
        t('enterprise_home_plan_unlimited_global'),
        crossRegion,
        t('enterprise_home_plan_batch_custom'),
      ],
      action: t('enterprise_home_plan_action_contact'),
      customized: true,
    })
  }

  const plans = localPlanProfiles.map((profile) => {
    const rights = catalog?.rights?.[profile.rightsKey]
    const backendPrice = findPrice(catalog?.localPrices, profile.priceLevel, currency)
    return {
      name: t(profile.nameKey),
      quota: t('enterprise_home_plan_quota_local', { count: formatCharactersFromRights(rights?.charsMaxLimit, locale) }),
      price: getDisplayPrice(backendPrice),
      period: t('enterprise_home_plan_period'),
      features: [
        t('enterprise_home_plan_seats_local', { count: formatRightsNumber(rights?.memberCountLimit, locale) }),
        t('enterprise_home_plan_batch', { count: formatRightsNumber(rights?.batchTransMaxLimit, locale) }),
      ],
      action: t('enterprise_home_plan_action_open'),
      choiceId: backendPrice?.choiceId,
    }
  })

  return plans.concat({
    name: t('enterprise_home_plan_custom'),
    quota: t('enterprise_home_plan_custom_quota'),
    price: t('enterprise_home_plan_custom_price'),
    period: t('enterprise_home_plan_custom_period'),
    features: [
      t('enterprise_home_plan_unlimited_local'),
      t('enterprise_home_plan_custom_contact'),
    ],
    action: t('enterprise_home_plan_action_contact'),
    customized: true,
  })
}

export function getEnterpriseComparison(
  edition: SiteEdition,
  mode: EnterprisePricingMode,
  catalog: EnterpriseCatalogData | undefined,
  t: EnterpriseTranslate,
  locale = 'zh-CN',
): EnterpriseComparisonSection[] {
  const isVip = edition === 'vip'
  const globalMode = mode === 'global'
  const all = [true, true, true, true]
  const rightsProfiles = getRightsProfiles(mode, catalog)
  const quotaValues = rightsProfiles.map(rights => formatCharactersFromRights(rights?.charsMaxLimit, locale))
  quotaValues.push(t('enterprise_home_comparison_custom'))
  const seatValues = rightsProfiles.map(rights => t('enterprise_home_comparison_people', { count: formatRightsNumber(rights?.memberCountLimit, locale) }))
  seatValues.push(t(globalMode ? 'enterprise_home_plan_unlimited_global' : 'enterprise_home_plan_unlimited_local'))

  const sections: EnterpriseComparisonSection[] = [
    {
      title: t('enterprise_home_comparison_translation'),
      rows: [
        {
          label: t(globalMode ? 'enterprise_home_comparison_quota_global' : 'enterprise_home_comparison_quota_local'),
          values: quotaValues,
        },
        { label: t('enterprise_home_comparison_text'), values: all },
        { label: t('enterprise_home_comparison_formats'), values: all },
        { label: t('enterprise_home_comparison_image'), values: all },
        { label: t('enterprise_home_comparison_editing'), values: all },
        { label: t('enterprise_home_comparison_priority'), values: [false, true, true, true] },
      ],
    },
    {
      title: t('enterprise_home_comparison_collaboration'),
      rows: [
        {
          label: t(globalMode ? 'enterprise_home_comparison_seats_global' : 'enterprise_home_comparison_seats_local'),
          values: seatValues,
        },
        { label: t('enterprise_home_comparison_permissions'), values: all },
        { label: t(globalMode ? 'enterprise_home_comparison_usage_global' : 'enterprise_home_comparison_usage_local'), values: all },
        { label: t('enterprise_home_comparison_subteams'), values: all },
      ],
    },
    {
      title: t('enterprise_home_comparison_security'),
      rows: [
        {
          label: globalMode
            ? t('enterprise_home_comparison_residency_global')
            : t(isVip ? 'enterprise_home_comparison_residency_cn' : 'enterprise_home_comparison_residency_ai'),
          values: all,
        },
        { label: t('enterprise_home_comparison_encryption'), values: all },
        { label: t('enterprise_home_comparison_deletion'), values: all },
      ],
    },
  ]

  if (globalMode) {
    sections.push({
      title: t('enterprise_home_comparison_global_title'),
      rows: [
        { label: t('enterprise_home_comparison_global_members'), values: all },
        { label: t('enterprise_home_comparison_global_allocation'), values: all },
        { label: t('enterprise_home_comparison_global_residency'), values: all },
        {
          label: t('enterprise_home_comparison_global_billing', { site: isVip ? '.vip' : '.ai' }),
          values: Array(4).fill(t(isVip ? 'enterprise_home_comparison_invoice_vat' : 'enterprise_home_comparison_invoice_global')),
        },
      ],
    })
  }

  sections.push({
    title: t('enterprise_home_comparison_support'),
    rows: [
      { label: t(isVip ? 'enterprise_home_comparison_invoice_cn' : 'enterprise_home_comparison_invoice_ai'), values: all },
      { label: t('enterprise_home_comparison_support_method'), values: [t('enterprise_home_comparison_ticket'), t('enterprise_home_comparison_ticket_chat'), t('enterprise_home_comparison_manager'), t('enterprise_home_comparison_manager')] },
      { label: t('enterprise_home_comparison_sla'), values: [false, false, '99.9%', '99.9%'] },
      { label: t('enterprise_home_comparison_glossary_help'), values: [false, true, true, true] },
    ],
  })

  return sections
}

export function getEnterpriseFaqs(edition: SiteEdition, contactEmail: string, t: EnterpriseTranslate): EnterpriseFaq[] {
  const isVip = edition === 'vip'
  return [
    ...baseFaqKeys.map(key => getFaq(t, key)),
    {
      question: t('enterprise_home_faq_encryption_question'),
      answer: t(isVip ? 'enterprise_home_faq_encryption_answer_cn' : 'enterprise_home_faq_encryption_answer_global'),
    },
    {
      question: t(isVip ? 'enterprise_home_faq_data_question_cn' : 'enterprise_home_faq_data_question_global'),
      answer: t('enterprise_home_faq_data_answer'),
    },
    {
      question: t('enterprise_home_faq_cross_region_question'),
      answer: t(isVip ? 'enterprise_home_faq_cross_region_answer_cn' : 'enterprise_home_faq_cross_region_answer_global'),
    },
    ...trailingFaqKeys.map(key => getFaq(t, key)),
    {
      question: t('enterprise_home_faq_invoice_question'),
      answer: t(isVip ? 'enterprise_home_faq_invoice_answer_cn' : 'enterprise_home_faq_invoice_answer_global', { email: contactEmail }),
    },
  ]
}

export function getEnterprisePlanPrices(
  edition: SiteEdition,
  mode: EnterprisePricingMode,
  catalog: EnterpriseCatalogData | undefined,
  t: EnterpriseTranslate,
  locale = 'zh-CN',
) {
  if (mode === 'global') {
    return getEnterprisePlans(edition, 'global', catalog, t, locale)
      .map(plan => plan.customized ? t('enterprise_home_plan_custom_consultation') : (plan.price === '--' ? '--' : `${plan.price}${plan.period}`))
  }
  return getEnterprisePlans(edition, 'local', catalog, t, locale)
    .map(plan => plan.customized ? t('enterprise_home_plan_quote') : (plan.price === '--' ? '--' : `${plan.price}${plan.period}`))
}
